import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {dateOnly} from "@/lib/format.tsx";
import {Credentials, CredentialTypeLabel} from "@/lib/types/credentials.ts";
import {VaultEmptyTableBody} from "@/pages/vault/VaultEmptyTableBody.tsx";

import {Table} from "../../components/table/Table.tsx";
import {DeleteCredentialButton} from "./vaultComponents/DeleteCredentialsButton.tsx";

import styles from "./VaultTable.module.scss";

interface VaultTableProps extends LimitAndOffset {
  credentials: Credentials[];
  handleIdClick: (id: string) => void;
  setLaf(laf: LimitAndOffset): void;
  total: number;
  dataLoading: boolean;
}

export const VaultTable = ({
  credentials,
  handleIdClick,
  offset,
  limit,
  setLaf,
  total,
  dataLoading,
}: VaultTableProps) => {
  const {user} = useCurrentUserContext();

  return (
    <div>
      <Table
        rows={credentials}
        minWidth="800px"
        fixedWidth
        specs={[
          {
            fieldName: "display_name",
            meta: {
              headerClassName: styles.nameColumn,
            },
            accessor: (obj) => (
              <Button
                variant={"linkBlue"}
                typography={"linkBold"}
                size={"text"}
                className={"ellipsis-overflow"}
                onClick={() => handleIdClick(obj.id)}
              >
                <Tooltip tooltipContent={obj.display_name} delayMs={500}>
                  {obj.display_name}
                </Tooltip>
              </Button>
            ),
            fieldTitle: "Item Name",
          },
          {
            fieldName: "credential_type",
            meta: {
              headerClassName: styles.typeColumn,
            },
            accessor: (obj) => CredentialTypeLabel[obj.credential_type],
            fieldTitle: "Type",
          },
          {
            fieldName: "service_variable_count",
            meta: {
              headerClassName: styles.usedInColumn,
            },
            accessor: (obj) =>
              `${obj.service_variable_count ? obj.service_variable_count : 0} Check${obj.service_variable_count === 1 ? "" : "s"}`,
            fieldTitle: "Used In",
          },
          {
            fieldName: "created_at",
            meta: {
              headerClassName: styles.dateColumn,
            },
            accessor: (obj) => dateOnly(obj.created_at, user!.current_date_format, user!.account!.timezone),
            fieldTitle: "Date Created",
          },
          {
            fieldName: "created_by.id",
            meta: {
              headerClassName: styles.createdByColumn,
            },
            accessor: (obj) => {
              const createdByName = obj.created_by ? `${obj.created_by.first_name} ${obj.created_by.last_name}` : " ";
              return (
                <Tooltip tooltipContent={createdByName} delayMs={500}>
                  <div className="ellipsis-overflow">{createdByName}</div>
                </Tooltip>
              );
            },
            fieldTitle: "Created By",
          },
          {
            fieldName: "delete",
            meta: {
              headerClassName: styles.actionColumn,
            },
            accessor: (obj) => (
              <>
                {user?.has_write_access && (
                  <DeleteCredentialButton
                    credentialId={obj.id}
                    dialogElement={`Are you sure you want to delete "${obj.display_name}"?`}
                    dialogTitle={"Delete Credential"}
                  />
                )}
              </>
            ),
          },
        ]}
        pagination={{offset, limit}}
        updatePagination={setLaf}
        totalRows={total}
      />
      {credentials.length === 0 && !dataLoading && <VaultEmptyTableBody />}
    </div>
  );
};
